import React, { useEffect } from 'react';
import { Routes, Route, useLocation, useParams, Navigate } from "react-router-dom";
import { firebaseConfig } from '../../constants/firebaseConfig';
import Welcome from '../../pages/public/Welcome';
import CreateSupermarket from '../../pages/private/supermarkets/Create';
import Carts from '../../pages/public/Carts';
import ShoppingListsPage from '../../pages/public/ShoppingListsPage';
import Login from '../../pages/public/Login';
import CreateProduct from '../../pages/private/products/Create';
import PublicProducts from '../../pages/public/PublicProducts';
import ProductAnalysis from '../../pages/public/ProductAnalysis';
import InventoryPublicPageWrapper from '../../pages/public/Inventory';
import CreateInventory from '../../pages/private/inventory/CreateInventory';
import EditInventoryItem from '../../pages/private/inventory/EditInventoryItem';
import SupermarketProducts from '../../pages/public/SupermarketProducts';
import Supermarkets from '../../pages/public/Supermarkets/Supermarkets';
import ProductDetailsPage from '../../pages/public/ProductDetails';
import ProductDetailsByProduct from '../../pages/public/ProductDetailsByProduct';
import ProductResolver from '../../pages/public/ProductResolver';
import SupermarketOrProductResolver from '../../pages/public/SupermarketOrProductResolver';
import CheckoutPage from '../../pages/public/CheckoutPage';
import PaymentPage from '../../pages/public/PaymentPage';
import UserOrderStatusPage from '../../pages/public/UserOrderStatusPage';
import OrderHistoryPage from '../../pages/public/OrderHistoryPage';
import EmployeeDashboard from '../../pages/employee/EmployeeDashboard';
import EmployeePackOrderPage from '../../pages/employee/EmployeePackOrderPage';
import ManagePackedOrdersPage from '../../pages/employee/ManagePackedOrdersPage';
import SearchResultsPage from '../../pages/public/SearchResultsPage'; // Import SearchResultsPage
import WipayResponsePage from '../../pages/public/WipayResponsePage'; // Add this import
import NotificationTest from '../../pages/public/NotificationTest';
import ReportPage from '../../pages/employee/ReportPage'; // <-- Change this line
import PrivacyPolicy from '../../pages/public/PrivacyPolicy';
import TermsOfService from '../../pages/public/TermsOfService';
import RefundPolicy from '../../pages/public/RefundPolicy';
import HowItWorks from '../../pages/public/HowItWorks';
import FAQ from '../../pages/public/FAQ';

import AdminRoute from './AdminRoute';
import EmployeeOrAdminRoute from './EmployeeOrAdminRoute'; 
import AdminLandingPage from '../../pages/admin/AdminLandingPage';
import AdminDashboard from '../../pages/admin/AdminDashboard';
import AdminUnprocessedPaymentsPage from '../../pages/admin/AdminUnprocessedPaymentsPage';
import SupermarketsPage from '../../pages/private/supermarkets/SupermarketsPage';
import ProductsPage from '../../pages/private/products/ProductsPage';
import ErrorPage from '../../pages/public/ErrorPage'; // Import ErrorPage
import { useAuth } from '../context/AuthContext'; // Import useAuth

// Create a new route component:
// function AdminOrEmployeeRoute({ element }) {
//   const { userRole } = useAuth();
//   if (userRole === 'Admin' || userRole === 'Employee') {
//     return element;
//   }
//   return <ErrorPage message="You are not authorized to view this page." />;
// }

function ReportsRoute() {
  const { userRole } = useAuth();
  if (userRole === 'Admin' || userRole === 'Employee') {
    return <ReportPage />;
  }
  return <ErrorPage message="You are not authorized to view this page." />;
}

/** Redirects /orders/:orderId to /order-status/:orderId (used by notification click_action links). */
function OrdersRedirect() {
  const { orderId } = useParams();
  return <Navigate to={`/order-status/${orderId}`} replace />;
}

export default function Router() {
    const location = useLocation();

    // Track page views with Google Analytics
    useEffect(() => {
        if (firebaseConfig.measurementId && typeof window !== 'undefined' && window.gtag) {
            window.gtag('config', firebaseConfig.measurementId, {
                page_path: location.pathname + location.search,
            });
        }
    }, [location]);

    return (
        <Routes location={location} key={location.pathname}>
            {/* Public Routes */}
            <Route exact path="/" element={<Welcome />} />
            <Route path="/products" element={<PublicProducts />} />
            <Route path="/products/analysis/:slug" element={<ProductAnalysis />} />
            <Route path="/product/:slug" element={<ProductResolver />} />
            <Route path="/carts" element={<Carts />} />
            <Route path="/shopping-lists" element={<ShoppingListsPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/search" element={<SearchResultsPage />} />
            <Route path="/supermarkets" element={<Supermarkets />} />
            <Route path="/supermarkets/:supermarketId/products" element={<SupermarketProducts />} />
            <Route path="/supermarkets/:supermarketId/products/:slug" element={<SupermarketOrProductResolver />} />
            <Route path="/checkout/:supermarketId/:cartInstanceId" element={<CheckoutPage />} /> 
            <Route path="/payment/:transactionId" element={<PaymentPage />} />
            <Route path="/order-status/:transactionId" element={<UserOrderStatusPage />} />
            <Route path="/orders/:orderId" element={<OrdersRedirect />} />
            <Route path="/order-history" element={<OrderHistoryPage />} />
            <Route path="/payment/response" element={<WipayResponsePage />} /> {/* <-- Add this line */}
            {process.env.NODE_ENV === 'development' && (
              <Route path="/notification-test" element={<NotificationTest />} />
            )}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/faq" element={<FAQ />} />

            {/* Inventory Routes - Protected for Admin or Employee */}
            <Route path="/inventory" element={<EmployeeOrAdminRoute />}>
                <Route index element={<InventoryPublicPageWrapper />} />
                <Route path="create" element={<CreateInventory />} />
                <Route path="edit/:itemId" element={<EditInventoryItem />} />
            </Route>

            {/* Product catalog: Admins + Employees (employees: view-only in UI; create/edit remain Admin-only below). */}
            <Route path="/admin/products" element={<EmployeeOrAdminRoute />}>
                <Route index element={<ProductsPage />} />
            </Route>

            {/* Admin Only Routes */}
            <Route path="/admin" element={<AdminRoute />}>
                <Route index element={<AdminLandingPage />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="supermarkets" element={<SupermarketsPage />} />
                <Route path="supermarkets/create" element={<CreateSupermarket />} />
                <Route path="supermarkets/edit/:id" element={<CreateSupermarket />} />
                <Route path="products/create" element={<CreateProduct />} />
                <Route path="products/edit/:id" element={<CreateProduct />} />
                <Route path="unprocessed-payments" element={<AdminUnprocessedPaymentsPage />} />
            </Route>

            {/* Employee Only Routes */}
            <Route path="/employee/dashboard" element={<EmployeeDashboard />} />
            <Route path="/employee/pack-order/:transactionId" element={<EmployeePackOrderPage />} />
            <Route path="/employee/manage-packed-orders" element={<ManagePackedOrdersPage />} />
            <Route path="/admin/reports" element={<ReportsRoute />} />
        </Routes>
    );
}
